// components/common/dropdowns/ProjectUserDropdown.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDownIcon } from 'lucide-react';
import { Menu, MenuButton, MenuItem, MenuItems, Portal } from '@headlessui/react';
import SearchInput from '../../../common/SearchInput';
import { ProjectUser } from 'types/project';
import UserAvatar from '../../../common/UserAvatar';


interface ProjectUserDropdownProps {
  users: ProjectUser[];
  currentUserUuid: string | null;
  onUserChange: (user: ProjectUser | null) => void;
  placeholder?: string;
  disabled?: boolean;
  showEmail?: boolean;
  showName?: boolean;
  allowUnassigned?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  showChevron?: boolean;
}

const sizeClasses = {
  sm: 'px-1 py-1 min-w-0',
  md: 'px-2 py-1 min-w-[100px]',
  lg: 'px-3 py-2 min-w-[120px]',
};

const avatarSizes = {
  sm: 'w-6 h-6 text-[10px] leading-none',
  md: 'w-7 h-7 text-xs leading-none',
  lg: 'w-8 h-8 text-sm leading-none',
};

const textSizes = {
  sm: 'text-xs',
  md: 'text-sm',
  lg: 'text-base',
};

const getFullName = (u: ProjectUser) =>
  `${u.firstName} ${u.lastName}`.trim();



const UserDropdown: React.FC<ProjectUserDropdownProps> = ({
  users,
  currentUserUuid,
  onUserChange,
  placeholder = 'Unassigned',
  disabled = false,
  showEmail = true,
  showName = true,
  allowUnassigned = true,
  size = 'md',
  className = '',
  showChevron = true,
}) => {
  
  const [searchTerm, setSearchTerm] = useState('');
  const [open, setOpen] = useState(false);

  const currentUser = useMemo(
    () => users.find((u) => u.appUserUuid === currentUserUuid) ?? null,
    [users, currentUserUuid]
  );

  const filteredUsers = useMemo(
    () =>
      users.filter((u) => {
        const name = getFullName(u).toLowerCase();
        return (
          name.includes(searchTerm.toLowerCase()) ||
          (u.email ? u.email.toLowerCase().includes(searchTerm.toLowerCase()) : false)
        );
      }),
    [users, searchTerm]
  );

  useEffect(() => {
    if (open) setSearchTerm('');
  }, [open]);

  return (
    <Menu as="div" className="relative inline-block text-left w-full">
      <MenuButton
        disabled={disabled}
        onClick={() => setOpen(true)}
        title={!showName && currentUser ? getFullName(currentUser) : undefined}
        className={`flex items-center ${showName ? 'gap-2.5 text-left' : 'justify-center'} w-full ${sizeClasses[size]} rounded hover:bg-primary-subtle transition disabled:opacity-50 focus:outline-none focus:ring-0 ${className}`}
      >
        {currentUser ? (
          <>
            <UserAvatar
              name={getFullName(currentUser)}
              profileImageURL={currentUser.profileImageURL || currentUser.avatar}
              className={avatarSizes[size]}
            />

            {showName && (
              <span className={`${textSizes[size]} truncate flex-1 min-w-0`}>
                {getFullName(currentUser)}
              </span>
            )}
          </>
        ) : (
          <div className="flex items-center justify-center">
            {showName ? (
              <span className={`${textSizes[size]} text-neutral-medium truncate`}>
                {placeholder}
              </span>
            ) : (
              <div className={`rounded-full border border-dashed border-neutral-light/20 flex items-center justify-center text-neutral-medium ${avatarSizes[size]}`}>
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
            )}
          </div>
        )}
        {showChevron && (
          <ChevronDownIcon className={`${showName ? 'ml-auto' : ''} w-4 h-4 shrink-0 text-neutral-medium`} />
        )}
      </MenuButton>

      <Portal>
        <MenuItems 
          anchor="bottom start"
          className="mt-2 w-[250px]
          z-[9999]
          bg-primary-variant
          border border-primary-subtle
          rounded-lg shadow-xl
          focus:outline-none overflow-hidden"
        >
          
          {/* Search Header */}
          <div className="p-2 border-b border-primary-subtle bg-primary-variant">
               <SearchInput
                 value={searchTerm}
                 onChange={(e) => setSearchTerm(e.target.value)}
                 placeholder="Search users"
                 className="bg-primary-subtle border-transparent"
                 autoFocus
               />
          </div>

          <div className="max-h-[250px] overflow-y-auto py-1.5 px-1">
            
            {/* Unassigned Option matching design */}
            {allowUnassigned && (
              <MenuItem>
                {({ focus }) => (
                  <button
                    onClick={() => onUserChange(null)}
                    className={`w-full px-3 py-2 flex items-center gap-3 transition-colors ${
                      focus ? 'bg-primary-subtle' : ''
                    }`}
                  >
                    <div className="w-7 h-7 rounded-full flex items-center shrink-0 justify-center text-neutral text-xs font-semibold bg-primary-subtle border border-primary-intermediate">
                      <svg className="w-3.5 h-3.5 text-neutral-medium" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    <div className="min-w-0 text-left flex flex-col justify-center">
                      <div className="truncate text-sm font-medium text-neutral leading-tight">Unassigned</div>
                    </div>
                  </button>
                )}
              </MenuItem>
            )}

            {/* User Options */}
            {filteredUsers.map((user) => {
              const name = getFullName(user);
              return (
                <MenuItem key={user.appUserUuid}>
                  {({ focus }) => (
                    <button
                      onClick={() => onUserChange(user)}
                      className={`w-full px-3 py-2 flex items-center gap-3 transition-colors ${
                        focus ? 'bg-primary-subtle' : ''
                      }`}
                    >
                      <UserAvatar
                        name={name}
                        profileImageURL={user.profileImageURL || user.avatar}
                        className="w-7 h-7 text-xs"
                      />
                      <div className="min-w-0 text-left flex flex-col justify-center">
                        <div className="truncate text-sm font-medium text-neutral leading-tight">{name}</div>
                      </div>
                    </button>
                  )}
                </MenuItem>
              );
            })}
          </div>
        </MenuItems>
      </Portal>
    </Menu>
  );
};

export default UserDropdown;
